/*
  This example requires some changes to your config:

  ```
  // tailwind.config.js
  module.exports = {
    // ...
    plugins: [
      // ...
      require('@tailwindcss/forms'),
    ],
  }
  ```
*/
import { Combobox } from "@headlessui/react";
import { MagnifyingGlassIcon } from "@heroicons/react/20/solid";
import { FaceFrownIcon } from "@heroicons/react/24/outline";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import cx from "../../utils/cx.ts";

type ItemDefinition = {
  label: string;
  category: string;
  url: string;
};

const items: ItemDefinition[] = [
  {
    label: "Create new source",
    category: "Sources",
    url: `/sources/new`,
  },
  {
    label: "Edit an existing source",
    category: "Sources",
    url: "/sources",
  },
  {
    label: "Edit an existing project",
    category: "Projects",
    url: "/projects",
  },
  {
    label: "Enroll a new contractor",
    category: "Enrollees",
    url: "/enrollees/new",
  },
  {
    label: "Generate a certificate for a contractor",
    category: "Enrollees",
    url: "/enrollees",
  },
  {
    label: "View all certificates",
    category: "Certificates",
    url: "/certificates",
  },
  {
    label: "View all reports",
    category: "Reports",
    url: "/reports",
  },
  {
    label: "Build a report",
    category: "Reports",
    url: `/reports/new`,
  },
];

export default function CommandBox() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    ref.current?.focus();
  }, [ref]);

  const filteredItems = items.filter((item) => {
    return item.label.toLowerCase().includes(query.toLowerCase());
  });

  const groups = filteredItems.reduce<Record<string, ItemDefinition[]>>(
    (groups, item) => {
      return {
        ...groups,
        [item.category]: [...(groups[item.category] || []), item],
      };
    },
    {},
  );

  return (
    <div className="mx-auto w-[450px] transform overflow-hidden rounded-xl bg-white shadow-2xl ring-1 ring-black ring-opacity-5 transition-all">
      <Combobox onChange={(item: ItemDefinition) => navigate(item.url)}>
        <div className="relative">
          <MagnifyingGlassIcon
            className="pointer-events-none absolute left-4 top-3.5 h-5 w-5 text-gray-400"
            aria-hidden="true"
          />
          <Combobox.Input
            className="h-12 w-full border-0 bg-transparent pl-11 pr-4 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm"
            placeholder="Search..."
            onChange={(event) => setQuery(event.target.value)}
            ref={ref}
          />
        </div>

        {filteredItems.length > 0 && (
          <Combobox.Options
            static
            className="max-h-100 scroll-pb-2 scroll-pt-11 space-y-2 overflow-y-auto pb-2"
          >
            {Object.entries(groups).map(([category, items]) => (
              <li key={category}>
                <h2 className="bg-gray-100 px-4 py-2.5 text-xs font-semibold text-gray-900">
                  {category}
                </h2>
                <ul className="mt-2 text-sm text-gray-800">
                  {items.map((item: ItemDefinition) => (
                    <Combobox.Option
                      key={item.url}
                      value={item}
                      className={({ active }) =>
                        cx(
                          "cursor-default select-none px-4 py-2",
                          active && "bg-indigo-600 text-white",
                        )
                      }
                    >
                      {item.label}
                    </Combobox.Option>
                  ))}
                </ul>
              </li>
            ))}
          </Combobox.Options>
        )}

        {query !== "" && filteredItems.length === 0 && (
          <div className="border-t border-gray-100 px-6 py-14 text-center text-sm sm:px-14">
            <FaceFrownIcon
              className="mx-auto h-6 w-6 text-gray-400"
              aria-hidden="true"
            />
            <p className="mt-4 font-semibold text-gray-900">No results found</p>
            <p className="mt-2 text-gray-500">
              We couldn’t find anything with that search term. Please try again.
            </p>
          </div>
        )}
      </Combobox>
    </div>
  );
}
